import React, { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import { format, differenceInSeconds } from 'date-fns';
import { api } from '../../server';
import { getConfig } from '../../utils/getClient';
import { useHospital } from '../../contexts/HospitalContext';
import Logo from '../../assets/erpsaude.png';
import '../../App.css';

// ── Helpers ────────────────────────────────────────────────────────────────

function toLabel(slug) {
  return slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function calculateTimeElapsed(horario, now) {
  const [hours, minutes, seconds] = horario.split(':').map(Number);
  const startTime = new Date(now);
  startTime.setHours(hours, minutes, seconds, 0);
  if (startTime > now) startTime.setDate(startTime.getDate() - 1);
  const total = differenceInSeconds(now, startTime);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Minutos permitidos por código Manchester
const MINUTOS_MANCHESTER = { '5': 0, '6': 10, '7': 60, '8': 120, '9': 240 };

// Retorna "elemento-piscando" se o tempo permitido foi excedido
function getUrgencyClass(val_triagem, horario, now) {
  if (!val_triagem) return '';
  const tempo = calculateTimeElapsed(horario, now);
  const [h, m, s] = tempo.split(':').map(Number);
  const totalMin = h * 60 + m + s / 60;
  const permitido = MINUTOS_MANCHESTER[val_triagem] ?? 0;
  return totalMin > permitido ? 'elemento-piscando' : '';
}

// Cor de fundo da linha por código Manchester
const COR_MANCHESTER = {
  '5': '#ff4747',
  '6': '#ffa947',
  '7': '#ffed47',
  '8': '#50ff47',
  '9': '#4778ff',
};

// Classe do badge de urgência
const BADGE_CLASS = {
  '5': 'urgency-badge urgency-imediato',
  '6': 'urgency-badge urgency-ate10',
  '7': 'urgency-badge urgency-ate60',
  '8': 'urgency-badge urgency-ate120',
  '9': 'urgency-badge urgency-ate240',
};

const LABEL_MANCHESTER = {
  '5': 'Imediato',
  '6': 'Até 10 min',
  '7': 'Até 60 min',
  '8': 'Até 120 min',
  '9': 'Até 240 min',
};

// ── Componente de filtro de setor ──────────────────────────────────────────

const MENU_PROPS = {
  PaperProps: { style: { maxHeight: 460, width: 260 } },
};

export function MultipleSelectCheckmarks({ listSetor, personName, setPersonName }) {
  const handleChange = (event) => {
    setPersonName(event.target.value);
  };

  return (
    <FormControl sx={{ m: 1, minWidth: 260 }} size="small">
      <InputLabel id="filtro-setor-label">Centro de Custo</InputLabel>
      <Select
        labelId="filtro-setor-label"
        id="filtro-setor"
        multiple
        value={personName}
        onChange={handleChange}
        input={<OutlinedInput label="Centro de Custo" />}
        renderValue={(selected) =>
          selected
            .map((codigo) => listSetor.find((r) => r.CODIGO_SETOR === codigo)?.NOME ?? '')
            .filter(Boolean)
            .join(', ')
        }
        MenuProps={MENU_PROPS}
        aria-label="Filtrar por centro de custo"
      >
        {listSetor.map((row) => (
          <MenuItem key={row.CODIGO_SETOR} value={row.CODIGO_SETOR}>
            <Checkbox checked={personName.includes(row.CODIGO_SETOR)} />
            <ListItemText primary={row.NOME} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// ── Relógio ────────────────────────────────────────────────────────────────

function Clock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <time dateTime={now.toISOString()} className="panel-clock" aria-label="Horário atual">
      {format(now, 'HH:mm:ss')}
    </time>
  );
}

// ── Tabela principal ───────────────────────────────────────────────────────

function TablePainel({ token }) {
  const [painel, setPainel] = useState([]);
  const [setores, setSetores] = useState([]);
  const [personName, setPersonName] = useState([]);
  const [now, setNow] = useState(new Date());

  // Atualiza relógio local a cada segundo
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Busca setores uma vez
  useEffect(() => {
    const config = getConfig(token);
    api.get('/painel/setor', config)
      .then((res) => { if (res.data) setSetores(res.data); })
      .catch((err) => console.error('Erro ao buscar setores:', err.message));
  }, [token]);

  // Busca painel a cada 60s ou quando filtro mudar
  useEffect(() => {
    const config = getConfig(token);

    const fetchPainel = async () => {
      try {
        const response = await api.post('/painel/painel_atendimento', { codigo_setor: personName }, config);
        if (response.data) setPainel(response.data);
      } catch (error) {
        console.error('Erro ao buscar painel de atendimento:', error.message);
      }
    };

    fetchPainel();
    const intervalId = setInterval(fetchPainel, 60000);
    return () => clearInterval(intervalId);
  }, [token, personName]);

  return (
    <>
      {/* Barra de filtros + contagem */}
      <div className="filter-bar">
        {setores.length > 0 && (
          <MultipleSelectCheckmarks
            listSetor={setores}
            personName={personName}
            setPersonName={setPersonName}
          />
        )}
        <div className="panel-count-bar" role="status" aria-live="polite">
          <span className="panel-count-number">{painel.length}</span>
          <span className="panel-count-label">
            paciente{painel.length !== 1 ? 's' : ''} aguardando
          </span>
        </div>
      </div>

      {painel.length === 0 ? (
        <div className="panel-empty" role="status" aria-live="polite">
          <p>Nenhum paciente localizado</p>
        </div>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table aria-label="Pacientes aguardando atendimento">
            <TableHead>
              <TableRow>
                <TableCell>Paciente</TableCell>
                <TableCell>Classificação</TableCell>
                <TableCell>Registro</TableCell>
                <TableCell>Tempo aguardando</TableCell>
                <TableCell>Unidade / Setor</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {painel.map((row, index) => {
                const horario = format(new Date(row.HORA_ACOLHIMENTO), 'HH:mm:ss');
                const tempoEspera = calculateTimeElapsed(horario, now);
                const urgencyClass = getUrgencyClass(row.TRIAGEM_MANCHESTER, horario, now);
                const corFundo = COR_MANCHESTER[row.TRIAGEM_MANCHESTER];
                const badgeClass = BADGE_CLASS[row.TRIAGEM_MANCHESTER];
                const labelTriagem = LABEL_MANCHESTER[row.TRIAGEM_MANCHESTER] ?? 'Sem triagem';

                return (
                  <TableRow
                    key={`${row.NOME}-${index}`}
                    style={corFundo ? { backgroundColor: corFundo } : undefined}
                  >
                    <TableCell className={`cell-nome ${urgencyClass}`}>
                      {row.NOME}
                    </TableCell>
                    <TableCell className={urgencyClass}>
                      {badgeClass
                        ? <span className={badgeClass}>{labelTriagem}</span>
                        : <span style={{ color: '#475569' }}>{labelTriagem}</span>
                      }
                    </TableCell>
                    <TableCell className={urgencyClass}>
                      {format(new Date(row.DATA_ACOLHIMENTO), 'dd/MM/yyyy')}{' '}
                      {horario}
                    </TableCell>
                    <TableCell className={`cell-tempo ${urgencyClass}`}>
                      {tempoEspera}
                    </TableCell>
                    <TableCell className={urgencyClass}>
                      {row.NOME_SETOR}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

// ── Export principal ───────────────────────────────────────────────────────

export default function PainelAtendimento() {
  const { hospital, token } = useHospital();

  return (
    <div className="panel-container">
      <header className="panel-header" role="banner">
        <img src={Logo} alt="ERPSaúde" className="panel-logo" />
        <div className="panel-title-wrap">
          <h1 className="panel-title">Painel de Atendimento</h1>
          <span className="panel-hospital">{toLabel(hospital)}</span>
        </div>
        <Clock />
      </header>

      <main className="panel-main">
        <div className="panel-table-wrap">
          <TablePainel token={token} />
        </div>
      </main>
    </div>
  );
}
